/**
 * Check-In/Out Tabs Component
 * 入退室情報をタブで表示（仮想スクロール対応）
 */

import { Card, Tabs, Table, Badge, Avatar, Group, Text, ScrollArea, useMantineColorScheme } from '@mantine/core';
import { IconLogin, IconLogout, IconClock } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { ja } from 'date-fns/locale';
import { getGlassStyle } from '../styles/darkModeGlass';
import type { CheckInOutData } from '../types/enhanced';

const MotionCard = motion(Card);

export const CheckInOutTabs = ({ data }: { data: CheckInOutData }) => {
  const { colorScheme } = useMantineColorScheme();

  return (
    <MotionCard
      padding="sm"
      radius="sm"
      shadow="sm"
      style={{
        ...getGlassStyle(colorScheme),
        minHeight: '300px',
      }}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Tabs defaultValue="checkin" variant="pills">
        <Tabs.List mb="md">
          <Tabs.Tab value="checkin" leftSection={<IconLogin size={16} />}>
            入室中 ({data.current_checkins.length})
          </Tabs.Tab>
          <Tabs.Tab value="recent_in" leftSection={<IconLogin size={16} />}>
            最近入室
          </Tabs.Tab>
          <Tabs.Tab value="recent_out" leftSection={<IconLogout size={16} />}>
            最近退室
          </Tabs.Tab>
          <Tabs.Tab value="longest" leftSection={<IconClock size={16} />}>
            長時間滞在
          </Tabs.Tab>
        </Tabs.List>

        {/* 現在入室中 */}
        <Tabs.Panel value="checkin">
          <ScrollArea h={240}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>顧客</Table.Th>
                  <Table.Th>入室時刻</Table.Th>
                  <Table.Th>滞在時間</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.current_checkins.map((item) => (
                  <Table.Tr key={item.id}>
                    <Table.Td>
                      <Group gap="sm">
                        <Avatar color="blue" radius="xl">
                          {item.customer_name.charAt(0)}
                        </Avatar>
                        <Text size="sm">{item.customer_name}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{item.check_in_time}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color="teal">
                        {formatDistanceToNow(new Date(item.check_in_datetime), { locale: ja })}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Tabs.Panel>

        {/* 最近入室 */}
        <Tabs.Panel value="recent_in">
          <ScrollArea h={240}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>顧客</Table.Th>
                  <Table.Th>入室時刻</Table.Th>
                  <Table.Th>経過時間</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.recent_checkins.map((item) => (
                  <Table.Tr key={item.id}>
                    <Table.Td>
                      <Group gap="sm">
                        <Avatar color="green" radius="xl">
                          {item.customer_name.charAt(0)}
                        </Avatar>
                        <Text size="sm">{item.customer_name}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{item.check_in_time}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color="green">
                        {formatDistanceToNow(new Date(item.check_in_datetime), { locale: ja, addSuffix: true })}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Tabs.Panel>

        {/* 最近退室 */}
        <Tabs.Panel value="recent_out">
          <ScrollArea h={240}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>顧客</Table.Th>
                  <Table.Th>退室時刻</Table.Th>
                  <Table.Th>滞在時間</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.recent_checkouts.map((item) => (
                  <Table.Tr key={item.id}>
                    <Table.Td>
                      <Group gap="sm">
                        <Avatar color="gray" radius="xl">
                          {item.customer_name.charAt(0)}
                        </Avatar>
                        <Text size="sm">{item.customer_name}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{item.check_out_time}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color="blue">
                        {item.stay_duration ? `${item.stay_duration}分` : '-'}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Tabs.Panel>

        {/* 長時間滞在 */}
        <Tabs.Panel value="longest">
          <ScrollArea h={240}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>顧客</Table.Th>
                  <Table.Th>入室時刻</Table.Th>
                  <Table.Th>滞在時間</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.longest_stays.map((item) => (
                  <Table.Tr key={item.id}>
                    <Table.Td>
                      <Group gap="sm">
                        <Avatar color="orange" radius="xl">
                          {item.customer_name.charAt(0)}
                        </Avatar>
                        <Text size="sm">{item.customer_name}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{item.check_in_time}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="filled" color="orange">
                        {item.duration_minutes}分
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Tabs.Panel>
      </Tabs>
    </MotionCard>
  );
};
